import { normalize, type Vec3 } from '@rcs/domain';
import type { SampledTrack } from '@rcs/track-geometry';

/** サンプル軌道上の 1 点の幾何情報。 */
export interface TrackPoint {
  readonly position: Vec3;
  /** 単位接線 T */
  readonly tangent: Vec3;
  /** 上方向 U（T に直交） */
  readonly up: Vec3;
  /** 曲率ベクトル dT/ds [1/m] */
  readonly curvature: Vec3;
}

function read(array: Float64Array, index: number): Vec3 {
  return [array[index * 3] ?? 0, array[index * 3 + 1] ?? 0, array[index * 3 + 2] ?? 0];
}

function mix(a: Vec3, b: Vec3, t: number): Vec3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * 弧長 `s` での軌道上の点を返す。サンプル間は線形補間し、接線・上方向は正規化し直す。
 * 周回コースでは `s` を全長で剰余する。開いたコースでは `[0, 全長]` に丸める。
 */
export function lookupTrack(track: SampledTrack, s: number): TrackPoint {
  const length = track.length;
  const wrapped = track.closed
    ? ((s % length) + length) % length
    : Math.min(Math.max(s, 0), length);
  const index = Math.min(Math.max(Math.floor(wrapped / track.spacing), 0), track.count - 2);
  const s0 = track.s[index] ?? 0;
  const s1 = track.s[index + 1] ?? s0;
  const fraction = s1 > s0 ? Math.min(Math.max((wrapped - s0) / (s1 - s0), 0), 1) : 0;
  const tangent = normalize(
    mix(read(track.tangent, index), read(track.tangent, index + 1), fraction),
  );
  const rawUp = mix(read(track.up, index), read(track.up, index + 1), fraction);
  // 補間後の上方向を、接線に直交する単位ベクトルへ直す
  const along = rawUp[0] * tangent[0] + rawUp[1] * tangent[1] + rawUp[2] * tangent[2];
  const up = normalize([
    rawUp[0] - along * tangent[0],
    rawUp[1] - along * tangent[1],
    rawUp[2] - along * tangent[2],
  ]);
  return {
    position: mix(read(track.position, index), read(track.position, index + 1), fraction),
    tangent,
    up,
    curvature: mix(read(track.curvature, index), read(track.curvature, index + 1), fraction),
  };
}
